import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Works from './pages/Works';
import WorkDetail from './pages/WorkDetail';
import Commissions from './pages/Commissions';
import Contact from './pages/Contact';
import Bio from './pages/Bio';
import WorksAdmin from './pages/admin/WorksAdmin';
import MobileMenu from './components/MobileMenu';
import './App.css';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  background-color: #fff;
  box-sizing: border-box;
`;

const HeaderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: #fff;
  height: 70px;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 1300px;
  margin: 70px auto 0;
  width: 100%;
  box-shadow: none;
  background-color: #fff;
  position: relative;
  height: calc(100vh - 70px);
  box-sizing: border-box;
`;

const SidebarWrapper = styled.div`
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-650px);
  width: 200px;
  height: calc(100vh - 70px);
  z-index: 90;
  background-color: #fff;
  box-sizing: border-box;
  
  @media (max-width: 1300px) {
    left: 0;
    transform: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 200px;
  padding: 0;
  background-color: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1300px) {
    margin-left: 200px;
  }

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 25px 25px 25px 15px;

  @media (max-width: 1300px) {
    padding: 25px 15px;
  }
`;

const AnimatedRoutes = () => {
  const location = useLocation();
  const baseTitle = "Rory Wainwright Johnston"; 
  const tagline = "Conductor · Composer";
  const defaultDescription = "Official website of Rory Wainwright Johnston, conductor and composer based in Berlin. Explore works, biography, and commission information.";

  useEffect(() => {
    window.scrollTo(0, 0);

    let pageName = "Home";
    let pageDescription = defaultDescription;
    const path = location.pathname;

    if (path === '/') {
      pageName = 'Home';
    } else if (path === '/bio') {
      pageName = 'Biography';
      pageDescription = `Biography of conductor and composer Rory Wainwright Johnston.`;
    } else if (path.startsWith('/works/')) {
      pageName = 'Work Detail';
      pageDescription = `Details for a musical work by Rory Wainwright Johnston.`;
    } else if (path === '/works') {
      pageName = 'Works';
      pageDescription = `Explore the list of musical works by composer Rory Wainwright Johnston.`;
    } else if (path === '/commissions') {
      pageName = 'Commissions';
      pageDescription = `Information on commissioning new musical works from Rory Wainwright Johnston.`;
    } else if (path === '/contact') {
      pageName = 'Contact';
      pageDescription = `Contact conductor and composer Rory Wainwright Johnston.`;
    } else if (path.startsWith('/admin')) {
      pageName = 'Admin';
      pageDescription = defaultDescription; 
    }

    document.title = `${pageName} | ${baseTitle} | ${tagline}`;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', pageDescription);

  }, [location.pathname]);
  
  return (
    <PageContainer key={location.pathname}> 
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/bio" element={<Bio />} />
        <Route path="/works" element={<Works />} />
        <Route path="/works/:slug" element={<WorkDetail />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin/works" element={<ProtectedRoute element={<WorksAdmin />} />} />
      </Routes>
    </PageContainer>
  );
};

function App() {
  // State for mobile menu visibility
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Function to toggle the mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <Router>
      <AppContainer>
        <HeaderWrapper>
          {/* Pass toggle function to Header */}
          <Header onMenuToggle={toggleMobileMenu} />
        </HeaderWrapper>
        <ContentContainer>
          <SidebarWrapper>
            <Sidebar />
          </SidebarWrapper>
          <MainContent>
            <AnimatedRoutes />
          </MainContent>
          {/* Conditionally render MobileMenu */}
          <MobileMenu isOpen={isMobileMenuOpen} onClose={toggleMobileMenu} />
        </ContentContainer>
      </AppContainer>
    </Router>
  );
}

export default App;